import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#initPaymentRouting);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#initPaymentRouting);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
  #initPaymentRouting = () => {
  const DEBUG = false;
  const log = (...args) => DEBUG && console.log(...args);

  const dialog = this.refs?.dialog;
  if (!dialog) return;

  const countrySelect = dialog.querySelector('#cart-shipping-country');
  const checkoutButton = dialog.querySelector('#checkout');
  const accelerated = dialog.querySelector('.additional-checkout-buttons');
  const routingMessage = dialog.querySelector('#cart-routing-message');

  log('[router] initPaymentRouting', {
    hasCountrySelect: !!countrySelect,
    hasCheckoutButton: !!checkoutButton
  });

  if (!countrySelect || !checkoutButton) return;

  const updateUI = (reason) => {
    const isUS = countrySelect.value === 'US';
    const buttonText = checkoutButton.querySelector('.button-text');

    log('[router][updateUI]', reason, { value: countrySelect.value });

    if (buttonText) buttonText.textContent = isUS ? 'Check out' : 'Continue to secure payment';
    if (accelerated) accelerated.style.display = isUS ? '' : 'none';

    if (routingMessage) {
      routingMessage.hidden = isUS;
      routingMessage.textContent = isUS ? '' : 'International orders are processed via secure payment on the next page.';
    }
  };

  // Avoid binding twice across multiple opens
  if (dialog.dataset.routerBound === '1') {
    updateUI('reopen');
    return;
  }
  dialog.dataset.routerBound = '1';

  countrySelect.addEventListener('change', () => updateUI('country change'));

  checkoutButton.addEventListener('click', async (event) => {
    if (countrySelect.value === 'US') return;

    log('[router][event] click checkout (drawer)', countrySelect.value);

    event.preventDefault();
    event.stopImmediatePropagation();

    const buttonText = checkoutButton.querySelector('.button-text');
    if (buttonText) buttonText.textContent = 'Redirecting…';

    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const res = await fetch('https://payment-router.francesco-827.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipping_country: countrySelect.value,
          route: 'stripe_non_us',
          cart: {
            currency: cart.currency,
            total_price: cart.total_price,
            items: (cart.items || []).map(item => ({
              product_id: item.product_id,
              variant_id: item.variant_id,
              product_title: item.product_title,
              variant_title: item.variant_title,
              quantity: item.quantity,
              price: item.price,
              line_price: item.line_price,
              sku: item.sku,
              image: item.image,
              requires_shipping: item.requires_shipping
            }))
          }
        })
      }).then(r => r.json());

      log('[router] worker response', res);

      if (res.redirect_url) window.location.href = res.redirect_url;
      else alert('Payment routing error. Please try again.');
    } catch (err) {
      log('[router] worker error', err);
      alert('Could not connect to payment router.');
    }
  }, true);

  updateUI('open');
};

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
